/**
 * Interim API path rewrite shim.
 *
 * The backend was refactored from a flat `/api/<domain>` layout to a mostly
 * versioned one (`/api/v2/<domain>`), with a handful of domains kept on the
 * legacy `/api/` prefix and a few endpoints restructured. The frontend still
 * calls the old paths from ~367 scattered string literals.
 *
 * Rather than touch every call site (that happens in the upcoming endpoints +
 * zustand refactor, which will delete this file), we rewrite old -> new paths
 * centrally at request time — applied in the axios interceptor and a global
 * fetch wrapper (see src/index.js).
 *
 * Strategy: ALLOWLIST. We only rewrite domains we've confirmed moved to v2;
 * anything else passes through untouched so we never break a valid legacy path.
 * Unmatched `/api/...` paths are logged in dev to surface gaps.
 *
 * See frontend/API_ENDPOINT_MIGRATION.md for the full mapping + known gaps.
 */

// Ordered explicit rewrites (restructures). First matching regex wins.
const EXPLICIT_RULES = [
  [/^\/api\/auth\/me\b/, "/api/v2/profile/me"],
  // profile update collapsed onto the canonical PUT /profile/me
  [/^\/api\/profile\/update\b/, "/api/v2/profile/me"],
  // form counts + submissions live on the forms domain, not under /admin
  [/^\/api\/admin\/forms\/counts/, "/api/v2/forms/counts"],
  [/^\/api\/admin\/forms\/submissions/, "/api/v2/forms/submissions"],
  // partners moved under the admin/core router
  [/^\/api\/admin\/partners/, "/api/v2/admin/core/partners"],
  // lead-score report is served by the CRM domain
  [/^\/api\/admin\/lead-scores/, "/api/v2/crm/lead-scores"],
  // public contact / coach-application submissions
  [/^\/api\/contact\/submit/, "/api/v2/forms/contact/submit"],
  [/^\/api\/coach-applications\/submit/, "/api/v2/coach-applications/submit"],
  // sales dashboard: metrics/pnl moved under admin/sales; invoices stay top-level
  [/^\/api\/sales\/metrics/, "/api/v2/admin/sales/metrics"],
  [/^\/api\/sales\/pnl/, "/api/v2/admin/sales/pnl"],
  [/^\/api\/sales\/invoices/, "/api/v2/sales/invoices"],
  // competitions admin moved under /admin/competitions (before generic rules)
  [/^\/api\/competitions\/admin\/competitions/, "/api/v2/admin/competitions"],
  [/^\/api\/competitions\/admin\/questions/, "/api/v2/admin/competitions/questions"],
  // competitions router dropped the doubled "/competitions" segment
  [/^\/api\/admin\/competitions\/admin\/competitions/, "/api/v2/admin/competitions"],
  [/^\/api\/competitions\/competitions/, "/api/v2/competitions"],
  // AI drills folded into the LMS domain
  [/^\/api\/ai-drills/, "/api/v2/lms/drills"],
  // resources/* that moved under LMS (order matters: before the generic
  // /api/resources -> /api/v2/resources remap below)
  [/^\/api\/resources\/courses/, "/api/v2/lms/courses"],
  [/^\/api\/resources\/workshops/, "/api/v2/lms/workshops"],
  [/^\/api\/resources\/progress/, "/api/v2/lms/progress"],
  [/^\/api\/resources\/drills/, "/api/v2/lms/drills"],
  // mentor sub-resources that moved
  [/^\/api\/mentors\/logos/, "/api/v2/resources/logos"],
  [/^\/api\/mentors\/bookings/, "/api/v2/sessions/bookings"],
  // strategy-calls listing rename
  [/^\/api\/strategy-calls\/my-sessions/, "/api/v2/strategy-calls/my-bookings"],
];

// Domains confirmed migrated to /api/v2/<domain>. Only these are remapped.
const V2_DOMAINS = new Set([
  "auth",
  "profile",
  "dashboard",
  "payments",
  "mentors",
  "sessions",
  "blog",
  "cohorts",
  "resources",
  "lms",
  "discounts",
  "strategy-calls",
  "discovery-calls",
  "peers",
  "feedback",
  "forms",
  "support",
  "competitions",
  "candidate", // candidate notifications
  "crm",
  "partner",
  "geolocation",
  "calendar",
  "mentor-calendar",
]);

// Admin sub-domains that stay on the legacy /api prefix (NOT moved to v2).
const ADMIN_LEGACY = new Set(["mentor-analytics", "candidate-analytics"]);

// Legacy /api domains that intentionally stay (suppress the dev "no rule" warn).
const LEGACY_PASSTHROUGH = new Set([
  "subscriptions",
  "mentor-dashboard",
  "contact",
  "public",
  "images",
  "cleanup",
  "health",
]);

/** Rewrite a bare `/api/...` path (may include a query string). */
export function rewriteApiPath(path) {
  if (typeof path !== "string" || !path.startsWith("/api")) return path;
  // Already on the new scheme.
  if (path.startsWith("/api/v2/")) return path;

  // Preserve query string; rules match on the path portion only.
  const qIdx = path.indexOf("?");
  const pathname = qIdx === -1 ? path : path.slice(0, qIdx);
  const query = qIdx === -1 ? "" : path.slice(qIdx);

  const rewritten = rewritePathname(pathname);
  return rewritten + query;
}

function rewritePathname(pathname) {
  for (const [re, replacement] of EXPLICIT_RULES) {
    if (re.test(pathname)) return pathname.replace(re, replacement);
  }

  // Admin namespace: /api/admin/<x> -> /api/v2/admin/<x>, except legacy ones.
  const adminMatch = pathname.match(/^\/api\/admin\/([^/]+)/);
  if (adminMatch) {
    if (ADMIN_LEGACY.has(adminMatch[1])) return pathname;
    return pathname.replace(/^\/api\/admin\//, "/api/v2/admin/");
  }

  // Domain prefix remap for confirmed-migrated domains.
  const domainMatch = pathname.match(/^\/api\/([^/]+)/);
  if (domainMatch) {
    const domain = domainMatch[1];
    if (V2_DOMAINS.has(domain)) {
      return pathname.replace(/^\/api\//, "/api/v2/");
    }
    if (!LEGACY_PASSTHROUGH.has(domain) && process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn(`[api-shim] no rewrite rule for ${pathname} — passing through`);
    }
  }
  return pathname;
}

/**
 * Rewrite the `/api/...` portion inside a full URL (e.g. `${BACKEND_URL}/api/x`).
 * Non-API URLs and third-party URLs are returned unchanged.
 */
export function rewriteApiUrl(url) {
  if (typeof url !== "string") return url;
  const idx = url.indexOf("/api/");
  if (idx === -1) return url;
  const base = url.slice(0, idx);
  const apiPart = url.slice(idx);
  return base + rewriteApiPath(apiPart);
}
